import type {
  ExploreField,
  ExploreFieldOverride,
  ExploreFieldOverrides,
  ExploreSpec,
} from "./types.js";

export interface ExploreSpecSessionStateUpdateableFields {
  fieldOverrides: ExploreFieldOverrides | null;
}

/**
 * Merges an ExploreFieldOverride with the base ExploreField.
 *
 * @param field - The base ExploreField from the ExploreSpec
 * @param fieldOverride - The override from ExploreSpecSessionState
 * @returns The merged ExploreField with overrides applied
 */
export function mergeExploreFieldWithOverride(
  field: ExploreField,
  fieldOverride: ExploreFieldOverride | undefined,
): ExploreField {
  if (!fieldOverride) {
    return field;
  }

  const overrides = Object.fromEntries(
    Object.entries(fieldOverride).filter(([_, value]) => value != null),
  );

  return {
    ...field,
    ...overrides,
  };
}

/**
 * Merges ExploreSpecSessionState with the base ExploreSpec.
 *
 * @param spec - The base ExploreSpec
 * @param sessionState - The session state with field overrides
 * @returns The merged ExploreSpec with session state overrides applied
 */
export function mergeExploreSpecWithSessionState(
  spec: ExploreSpec,
  sessionState: ExploreSpecSessionStateUpdateableFields | null,
): ExploreSpec {
  const fieldOverrides = sessionState?.fieldOverrides;
  if (fieldOverrides == null) {
    return spec;
  }

  const fields = spec.fields.map((field) => {
    const override = fieldOverrides[field.id];
    return mergeExploreFieldWithOverride(field, override);
  });

  return {
    ...spec,
    fields,
  };
}
